/**
 * Cart Persistence Middleware
 * 
 * Automatically saves cart changes to localStorage for:
 * - Backup persistence for authenticated users
 * - Primary persistence for non-authenticated users
 * - Seamless offline support
 * 
 * MVP-friendly: Simple, automatic, no developer intervention needed
 */

import { localCartService } from '../../services/localCartService';

// Actions that should trigger cart persistence
const CART_ACTIONS_TO_PERSIST = [
  'cart/addToCartLocal',
  'cart/removeFromCartLocal', 
  'cart/updateQuantityLocal',
  'cart/hydrateCartFromStorage',
  'cart/mergeLocalCartOnLogin',
  'cart/fetchCartItems/fulfilled',
  'cart/addToCartAsync/fulfilled',
  'cart/updateCartItemAsync/fulfilled',
  'cart/removeFromCartAsync/fulfilled'
];

// Actions that should clear localStorage cart
const CART_ACTIONS_TO_CLEAR = [
  'cart/clearCart',
  'auth/logout'
];

/**
 * Cart Persistence Middleware
 * 
 * Intercepts cart actions and automatically persists to localStorage
 */
const cartPersistenceMiddleware = (store) => (next) => (action) => {
  // Execute the action first
  const result = next(action);
  
  // Get the updated state after action
  const state = store.getState();
  const { cart, auth } = state;
  
  try {
    // Handle cart clearing actions
    if (CART_ACTIONS_TO_CLEAR.some(actionType => action.type === actionType)) {
      localCartService.clearCart();
      
      if (import.meta.env.DEV) {
        console.log('🗑️ [CartPersistence] Cleared localStorage cart due to:', action.type);
      }
      
      return result;
    }
    
    // Handle cart persistence actions
    if (CART_ACTIONS_TO_PERSIST.some(actionType => action.type === actionType)) {
      const shouldPersist = shouldPersistCart(cart, auth, action.type);
      
      if (shouldPersist) {
        const success = localCartService.saveCart(cart.items || []);
        
        if (import.meta.env.DEV && success) {
          console.log('💾 [CartPersistence] Persisted cart to localStorage:', {
            action: action.type,
            itemCount: cart.items?.length || 0,
            isAuthenticated: auth.is_authenticated
          });
        }
      }
    }
  } catch (error) {
    // Never let persistence errors break the app
    console.error('❌ [CartPersistence] Error in persistence middleware:', error);
  }
  
  return result;
};

/**
 * Determine if cart should be persisted based on state and action
 * @param {Object} cart - Cart state
 * @param {Object} auth - Auth state  
 * @param {string} actionType - Redux action type
 * @returns {boolean} Should persist
 */
function shouldPersistCart(cart, auth, actionType) {
  // Always persist for non-authenticated users
  if (!auth.is_authenticated) {
    return true;
  }
  
  // For authenticated users, persist based on action type
  switch (actionType) {
    case 'cart/addToCartLocal':
    case 'cart/removeFromCartLocal':
    case 'cart/updateQuantityLocal':
      // Local actions should always persist (fallback scenarios)
      return true;
      
    case 'cart/hydrateCartFromStorage':
      // Don't persist hydration (would be circular)
      return false;
      
    case 'cart/mergeLocalCartOnLogin':
      // Don't persist merge result immediately (will be synced to DB)
      return false;
      
    case 'cart/fetchCartItems/fulfilled':
    case 'cart/addToCartAsync/fulfilled':
    case 'cart/updateCartItemAsync/fulfilled':
    case 'cart/removeFromCartAsync/fulfilled':
      // Persist DB actions as backup for authenticated users
      return true;
      
    default:
      return true;
  }
}

/**
 * Enhanced cart persistence middleware with throttling
 * 
 * Prevents excessive localStorage writes during rapid actions
 */
const createThrottledCartPersistenceMiddleware = (throttleMs = 500) => {
  let persistTimeout = null;
  let pendingItems = null;
  
  return (store) => (next) => (action) => {
    const result = next(action);
    const state = store.getState();
    const { cart, auth } = state;
    
    try {
      // Handle clearing actions immediately
      if (CART_ACTIONS_TO_CLEAR.some(actionType => action.type === actionType)) {
        if (persistTimeout) {
          clearTimeout(persistTimeout);
          persistTimeout = null;
        }
        
        localCartService.clearCart();
        
        if (import.meta.env.DEV) {
          console.log('🗑️ [CartPersistence] Cleared localStorage cart (immediate)');
        }
        
        return result;
      }
      
      // Handle persistence actions with throttling
      if (CART_ACTIONS_TO_PERSIST.some(actionType => action.type === actionType)) {
        const shouldPersist = shouldPersistCart(cart, auth, action.type);
        
        if (shouldPersist) {
          pendingItems = cart.items || [];
          
          // Clear existing timeout
          if (persistTimeout) {
            clearTimeout(persistTimeout);
          }
          
          // Set new timeout for throttled persistence
          persistTimeout = setTimeout(() => {
            if (pendingItems) {
              const success = localCartService.saveCart(pendingItems);
              
              if (import.meta.env.DEV && success) {
                console.log('💾 [CartPersistence] Throttled persist completed:', {
                  itemCount: pendingItems.length,
                  isAuthenticated: auth.is_authenticated
                });
              }
              
              pendingItems = null;
              persistTimeout = null;
            }
          }, throttleMs);
        }
      }
    } catch (error) {
      console.error('❌ [CartPersistence] Error in throttled persistence middleware:', error);
    }
    
    return result;
  };
};

export default cartPersistenceMiddleware;
export { createThrottledCartPersistenceMiddleware };